import { Action, ThunkAction, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import snackbarReducer from "components/action-snackbar/action-snackbar-slice";
import confirmDialogReducer from "components/confirm-dialog/confirm-dialog.slice";
import filtersReducer from "components/filters-bar/filters-slice";
import productPictureReducer from "components/product-picture-icon/product-picture.slice";
import sectionSideMenuReducer from "components/section-side-menu/section-side-menu.slice";
import wordProcessorSlice from "components/word-processor-field/word-processor.slice";
import quickLauncherReducer from "features/app-layout/quick-launcher/quick-launcher.slice";
import authReducer from "features/auth/auth-slice";
import billingFiltersReducer from "features/billing/invoices/billing-filters-slice";
import callAttachmentsReducer from "features/calls/dialstack/call-attachments.slice";
import callNotesReducer from "features/calls/dialstack/call-notes.slice";
import callTagsReducer from "features/calls/dialstack/call-tags.slice";
import deviceCallReducer from "features/calls/dialstack/device-call.slice";
import hiddenCallsReducer from "features/calls/dialstack/hidden-calls.slice";
import incomingCallPopReducer from "features/calls/dialstack/incoming-call-pop.slice";
import missedCallsReducer from "features/calls/dialstack/missed-calls.slice";
import outboundCallProgressReducer from "features/calls/dialstack/outbound-call-progress.slice";
import collectionsFiltersReducer from "features/collections/collections-list/collections-filters-slice";
import outreachScheduleFiltersReducer from "features/collections/outreach-schedule/outreach-schedule-filters-slice";
import inboxReducer from "features/inbox/inbox-slice";
import leadsSlice from "features/leads-ui/leads-ui.slice";
import medicalScribeSlice from "features/medical-scribe/medical-scribe.slice";
import notificationsSlice from "features/notifications/notifications.slice";
import activeVisitDrawerReducer from "features/patients/active-visit-drawer/active-visit-drawer.slice";
import patientHeaderSlice from "features/patients/patient-header/patient-header-slice";
import patientFiltersReducer from "features/patients/patients-list/patient-filters-slice";
import refundReceiptModalReducer from "features/patients/sections/patient-billing/refund-receipt/refund-receipt-modal.slice";
import patientChartNotesReducer from "features/patients/sections/patient-chart-notes/patient-chart-notes.slice";
import hearingAidsWizardReducer from "features/patients/sections/patient-hearing-aids/new-hearing-aid/new-hearing-aid-wizard/state/hearing-aids-wizard-slice";
import accountsReceivableSlice from "features/reports/accounts-receivable/accounts-receivable.slice";
import invoicesReceivableFiltersReducer from "features/reports/invoices-receivable/invoices-receivable-filters/invoices-receivable-filters.slice";
import schedulerV3Reducer from "features/scheduling-v3/state/scheduler-v3.slice";
import roomTrafficReducer from "features/scheduling/room-traffic/room-traffic.slice";
import appointmentFiltersReducer from "features/scheduling/scheduler-slice";
import { emptySplitApi } from "services/empty-split-api";
import { sunoCommsApi } from "services/suno-comms/suno-comms-api";
import { rtkQueryErrorLogger } from "utils/services.util";
import { viteEnv } from "utils/vite-env.util";

export const store = configureStore({
  reducer: {
    auth: authReducer,
    confirmDialog: confirmDialogReducer,
    productPicture: productPictureReducer,
    activeVisitDrawer: activeVisitDrawerReducer,
    patientHeader: patientHeaderSlice,
    patientFilters: patientFiltersReducer,
    patientChartNotes: patientChartNotesReducer,
    billingFilters: billingFiltersReducer,
    collectionsFilters: collectionsFiltersReducer,
    outreachScheduleFilters: outreachScheduleFiltersReducer,
    appointmentFilters: appointmentFiltersReducer,
    sectionSideMenu: sectionSideMenuReducer,
    filters: filtersReducer,
    hearingAidsWizard: hearingAidsWizardReducer,
    snackbar: snackbarReducer,
    incomingCallPop: incomingCallPopReducer,
    missedCalls: missedCallsReducer,
    outboundCallProgress: outboundCallProgressReducer,
    deviceCall: deviceCallReducer,
    callAttachments: callAttachmentsReducer,
    callNotes: callNotesReducer,
    callTags: callTagsReducer,
    hiddenCalls: hiddenCallsReducer,
    inbox: inboxReducer,
    wordProcessor: wordProcessorSlice,
    notifications: notificationsSlice,
    medicalScribe: medicalScribeSlice,
    accountsReceivable: accountsReceivableSlice,
    invoicesReceivableFilters: invoicesReceivableFiltersReducer,
    roomTraffic: roomTrafficReducer,
    schedulerV3: schedulerV3Reducer,
    leads: leadsSlice,
    refundReceiptModal: refundReceiptModalReducer,
    quickLauncher: quickLauncherReducer,
    [emptySplitApi.reducerPath]: emptySplitApi.reducer,
    [sunoCommsApi.reducerPath]: sunoCommsApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false // Ignore `A non-serializable value` errors,
    })
      .concat(emptySplitApi.middleware)
      .concat(sunoCommsApi.middleware)
      .concat(rtkQueryErrorLogger),
  devTools: viteEnv.VITE_ENV !== "prod"
});

setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
